import { ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { ILensesCardViewProps } from './lenses-card.models';
import { useStyles } from './lenses-card.styles';
import { LensesBrandLogo } from '../../../../../common/lenses-brand-logo';
import { EyeParams } from '../../../../../common/eye-params';
import { useEyeParamsByEye } from '../../../../../../hooks/use-eye-params-by-eye';
import { NoveltyLabel } from '../../../../../common/novelty-label';
import { LensesBrandType, SolutionsBrandType } from '../../../../../../graphql/lenses/models/get-lenses.models';
import { checkLabelEnabled } from '../utils/check-label-enabled';
import { OutOfSalesLabel } from '../../../out-of-sales/out-of-sales-label';
import { ProfileLabel } from '../../../profile-label';
import { ProductOwnerType } from '../../../../../../graphql/order/models/get-orders.models';

export const LensesCard = ({
  cardData,
  profileFirstName,
  profileLastName,
  profileIsActive = true,
  profileIsDeleted = false,
  profileColorIndex,
  hasKidProfiles = false,
}: ILensesCardViewProps): JSX.Element => {
  const classes = useStyles();
  const {
    brandId,
    leftEye,
    rightEye,
    brand: { labels },
  } = cardData;
  const { leftEyeColumnData, rightEyeColumnData, isOneEye } = useEyeParamsByEye(leftEye, rightEye);
  const [isLabelEnabled, setIsLabelEnabled] = useState<boolean>(false);
  const [labelName, setLabelName] = useState<string>('');
  const intl = useIntl();

  useEffect(() => {
    if (labels.length) {
      const label = labels.find(checkLabelEnabled);
      if (label && label.name.length) {
        setLabelName(label.name);
        setIsLabelEnabled(true);
      }
    }
  }, [labels]);

  const isSolutionsBrand = cardData.brandId === SolutionsBrandType.arl;
  const isProductLabel = !isSolutionsBrand && hasKidProfiles;

  return (
    <li className={classes.root} data-testid="lenses-card">
      {isProductLabel && (
        <ProfileLabel
          profile={{
            firstName: profileFirstName || '',
            lastName: profileLastName || '',
            isActive: profileIsActive,
            ownerType:
              profileFirstName === 'Consumer'
                ? ProductOwnerType.consumer
                : ProductOwnerType.dependent,
            colorIndex: profileColorIndex,
          }}
          isDeleted={profileIsDeleted}
        />
      )}
      <div>
        <LensesBrandLogo
          className={classes.lensesBrandLogo}
          brand={brandId as LensesBrandType}
        />
      </div>
      <div className={classes.eyeParamsContainer}>
        <EyeParams {...rightEyeColumnData} />
        {!isOneEye && <EyeParams {...leftEyeColumnData} />}
      </div>
      {isLabelEnabled && <NoveltyLabel label={labelName} />}
      {brandId === LensesBrandType.oneDate && (
        <OutOfSalesLabel
          message={intl.formatMessage(
            {
              id: 'lenses.1DATE.outOfSales.message',
            },
            {
              sup: (chunks: ReactNode) => <sup>{chunks}</sup>,
            },
          )}
          messageStyles={classes.messageStyles}
        />
      )}
    </li>
  );
};
